import React, { useState, useEffect } from 'react';
import styles from './UserCompliance.module.css';
import { useUsers } from '../../../hooks/useUsers';
import { usePermission } from '../../../hooks/usePermissions';
import api from '../../../services/api';
import { formatISTDateTime } from '../../../utils/datetime';

const UserCompliance = () => {
    const { raUsers, fetchRaUsers, loading } = useUsers();
    const { hasPermission } = usePermission();

    const [editingUser, setEditingUser] = useState(null);
    const [page, setPage] = useState(1);
    const [saving, setSaving] = useState(false);

    const limit = 10;

    useEffect(() => {
        fetchRaUsers(page, limit);
    }, [page, limit]);

    const handleEdit = async (user) => {
        try {
            const res = await api.get(`/users/ra/${user.userid}`);

            if (res.data) {
                setEditingUser(res.data);
            } else {
                alert("Failed to fetch RA details");
            }
        } catch (err) {
            console.error("Error fetching RA details:", err);
            alert("Error fetching RA details");
        }
    };

    const handleClose = () => {
        setEditingUser(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);

            const payload = {
                userid: editingUser.userid,
                isblocked: editingUser.isblocked,
                blockingnote: editingUser.blockingnote,
                ranumber: editingUser.ranumber,
                ravalidity: editingUser.ravalidity
            };
            const res = await api.put('/users/ra', payload);

            if (res.success) {
                fetchRaUsers(page, limit);
                handleClose();
            } else {
                alert(res.data?.message || "Failed to update user");
            }

        } catch (error) {
            console.error("Failed to update research analyst:", error);
            alert("Failed to update user.");
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        setEditingUser(prev => ({
            ...prev,
            [name]: value
        }));
    };



    const formatDateInput = (date) => {
        if (!date) return '';
        return new Date(date).toISOString().split('T')[0];
    };

    const SkeletonRows = () => (
        <>
            {[1, 2, 3, 4, 5].map((i) => (
                <tr key={i} className={styles.skeletonRow}>
                    <td><span className={`${styles.skel} ${styles.skel40}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel140}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel60}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel100}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel100}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel80}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel120}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel120}`} /></td>
                    <td><span className={`${styles.skel} ${styles.skel60}`} /></td>
                </tr>
            ))}
        </>
    );

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1>Research Analyst & Verification</h1>
                    <p>Manage blocking status and Research Analyst verification details</p>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.docTable}>
                    <thead>
                        <tr>
                            <th>User ID</th>
                            <th>Name</th>
                            <th>Blocked</th>
                            <th>Blocking Note</th>
                            <th>RA Number</th>
                            <th>RA Validity</th>
                            <th>Created At</th>
                            <th>Updated At</th>
                            <th className={styles.center}>Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <SkeletonRows />
                        ) : raUsers.length === 0 ? (
                            <tr className={styles.emptyRow}>
                                <td colSpan="9">
                                    <div className={styles.emptyIcon}>No Data</div>
                                    <h3>No users found</h3>
                                </td>
                            </tr>
                        ) : (
                            raUsers.map(user => (
                                <tr key={user.userid}>
                                    <td><span className={styles.cellId}>#{user.userid}</span></td>
                                    <td><div className={styles.cellName}>{user.name}</div></td>
                                    <td>
                                        <span
                                            className={`${styles.statusBadge} ${parseInt(user.isblocked) === 1 ? styles.inactive : styles.active}`}
                                        >
                                            {parseInt(user.isblocked) === 1 ? 'BLOCKED' : 'Active'}
                                        </span>
                                    </td>
                                    <td>{user.blockingnote || '-'}</td>
                                    <td>{user.ranumber || '-'}</td>
                                    <td>{user.ravalidity || '-'}</td>
                                    <td><span className={styles.dateText}>{formatISTDateTime(user.created_at)}</span></td>
                                    <td><span className={styles.dateText}>{formatISTDateTime(user.updated_at)}</span></td>
                                    <td className={styles.center}>
                                        <div className={styles.actionsCell}>
                                            {hasPermission("MODIFY_RESEARCHANALYST") && (
                                                <button
                                                    className={styles.actionButton}
                                                    onClick={() => handleEdit(user)}
                                                >
                                                    Edit
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit Modal */}
            {editingUser && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <div className={styles.modalHeader}>
                            <h2>Edit Compliance Details - ID: {editingUser.userid}</h2>
                            <button
                                type="button"
                                className={styles.modalClose}
                                onClick={handleClose}
                                disabled={saving}
                            >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSave}>
                            <div className={styles.modalBody}>
                                <div className={styles.formGroup}>
                                    <label>Is Blocked?</label>
                                    <select
                                        name="isblocked"
                                        value={editingUser.isblocked}
                                        onChange={handleChange}
                                        className={styles.select}
                                    >
                                        <option value="0">Active (0)</option>
                                        <option value="1">Blocked (1)</option>
                                    </select>
                                </div>

                                <div className={styles.formGroup}>
                                    <label>Blocking Note</label>
                                    <textarea
                                        name="blockingnote"
                                        value={editingUser.blockingnote || ''}
                                        onChange={handleChange}
                                        className={styles.textarea}
                                        placeholder="Reason for blocking..."
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>RA Number</label>
                                    <input
                                        type="text"
                                        name="ranumber"
                                        value={editingUser.ranumber || ''}
                                        onChange={handleChange}
                                        className={styles.input}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>RA Validity</label>
                                    <input
                                        type="date"
                                        name="ravalidity"
                                        value={formatDateInput(editingUser.ravalidity)}
                                        onChange={handleChange}
                                        className={styles.input}
                                    />
                                </div>
                            </div>

                            <div className={styles.modalFooter}>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={saving}
                                    className={styles.btnCancel}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className={styles.btnSave}
                                >
                                    {saving ? "Saving..." : "Save Changes"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserCompliance;